import React, { useState, useEffect } from 'react';
import {
  Box,
  FormControl,
  Select,
  MenuItem,
  Typography,
  Button,
  Divider,
  Tooltip,
  Chip,
  useTheme,
  useMediaQuery,
  Collapse,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions
} from '@mui/material';
import {
  Map as MapIcon,
  TrendingUp as TrendingIcon,
  LocalShipping as ShippingIcon,
  Storefront as StoreIcon,
  Verified as VerifiedIcon,
  NewReleases as NewIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  LocationOff as LocationOffIcon
} from '@mui/icons-material';
import { getProvinces, getCities } from '../constants/locations';
import MapDialog from './Map';
import {api} from '../services/api';

const QuickAccessToolbar = ({ 
  filterInputs,
  searchParams,
  setSearchParams,
  setFilterInputs
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [isExpanded, setIsExpanded] = useState(false);
  const [mapOpen, setMapOpen] = useState(false);
  const [distributors, setDistributors] = useState([]);
  const [loading, setLoading] = useState(false);
  const [availableCities, setAvailableCities] = useState([]);
  const [showNotFoundDialog, setShowNotFoundDialog] = useState(false);

  useEffect(() => {
    if (filterInputs?.province) {
      setAvailableCities(getCities('AR', filterInputs.province));
    }
  }, [filterInputs?.province]);

  const handleQuickAccess = (key) => {
    if (key === 'verified') {
      setFilterInputs(prev => ({
        ...prev,
        verifiedDistributors: !prev.verifiedDistributors
      }));
    }
  };

  const handleViewMap = async () => {
    const filters = {
      province: filterInputs.province || searchParams.get('province'),
      city: filterInputs.city || searchParams.get('city'),
      category: filterInputs.category || searchParams.get('category'),
      verified: filterInputs.verifiedDistributors || searchParams.get('verifiedDistributor') === 'true'
    };

    if (!filters.province) return;
    
    setLoading(true);
    try {
      const response = await api.getDistributors(filters);
      
      if (!response || response.length === 0) {
        setShowNotFoundDialog(true);
        return;
      }

      setDistributors(response);
      setMapOpen(true);
    } catch (error) {
      console.error('Error fetching distributors:', error);
      setShowNotFoundDialog(true);
    } finally {
      setLoading(false);
    }
  };

  const quickAccessOptions = [
    {
      icon: <VerifiedIcon sx={{ color: filterInputs.verifiedDistributors ? '#ffffff' : '#2196F3' }} />,
      label: 'Verificados',
      key: 'verified',
      tooltip: 'Distribuidores verificados',
      selected: filterInputs.verifiedDistributors
    }
  ];

  const NotFoundDialog = () => (
    <Dialog 
      open={showNotFoundDialog} 
      onClose={() => setShowNotFoundDialog(false)}
      PaperProps={{
        sx: {
          borderRadius: '12px',
          maxWidth: '400px',
          p: 1
        }
      }}
    >
      <DialogTitle sx={{ textAlign: 'center', pt: 3 }}>
        <LocationOffIcon 
          sx={{ 
            fontSize: '48px', 
            color: 'warning.main',
            mb: 2
          }} 
        />
        <Typography variant="h6" component="div">
          No se encontraron distribuidores
        </Typography>
      </DialogTitle>
      <DialogContent>
        <Typography 
          variant="body1" 
          align="center" 
          color="text.secondary"
          sx={{ mb: 2 }}
        >
          No hay distribuidores disponibles en la zona seleccionada
          {filterInputs.category && " para la categoría especificada"}
        </Typography>
      </DialogContent>
      <DialogActions sx={{ justifyContent: 'center', pb: 3 }}>
        <Button 
          onClick={() => setShowNotFoundDialog(false)}
          variant="contained"
          sx={{
            bgcolor: '#FF6F00',
            color: 'white',
            '&:hover': {
              bgcolor: '#F76707',
            },
            borderRadius: '8px',
            textTransform: 'none',
            px: 4
          }}
        >
          Entendido
        </Button>
      </DialogActions>
    </Dialog>
  );

  // Your existing component, wrapped in Collapse for mobile only
  const toolbarContent = (
    <Box
      sx={{
        display: 'flex',
        flexDirection: { xs: 'column', md: 'row' },
        alignItems: { xs: 'flex-start', md: 'center' },
        gap: { xs: 1.5, md: 2 },
        maxWidth: '1400px',
        margin: '0 auto',
        py: isMobile ? 0 : 0.5,
        mt:isMobile ? 1 : 0,
        px: isMobile ? 0 : 1
      }}
    >
        {!isMobile && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <MapIcon sx={{ color: '#FF6F00' }} />
            <Typography>Ver mapa de distribuidores</Typography>
          </Box>
        )}
      {/* Map section */}
      <Box
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', md: 'row' },
          alignItems: { xs: 'flex-start', md: 'center' },
          gap: { xs: 1.5, md: 2 },
          width: { xs: '100%', md: 'auto' },
          flex: { md: 2 },
        }}
      >
        <Box sx={{ 
          display: 'flex', 
          gap: 1, 
          width: '100%',
          flexDirection: { xs: 'column', sm: 'row' },
        }}>
          <FormControl size="small" sx={{ flex: 1 }}>
            <Select
              value={filterInputs.province}
              onChange={(e) => setFilterInputs(prev => ({
                ...prev,
                province: e.target.value
              }))}
              displayEmpty
              sx={{ 
                backgroundColor: '#f5f5f5',
                borderRadius: '8px',
                fontSize: '0.9rem',
                height: '36px',
                '& .MuiOutlinedInput-notchedOutline': {
                  border: 'none'
                },
                '&:hover .MuiOutlinedInput-notchedOutline': {
                  border: 'none'
                },
                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                  border: 'none'
                },
                boxShadow: '0 2px 4px rgba(0,0,0,0.05)'
              }}
            >
              <MenuItem value="">
                <em>Seleccionar provincia</em>
              </MenuItem>
              {getProvinces().map((prov) => (
                <MenuItem key={prov} value={prov}>
                  {prov}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl size="small" sx={{ flex: 1 }}>
            <Select
              value={filterInputs.city}
              onChange={(e) => setFilterInputs(prev => ({
                ...prev,
                city: e.target.value
              }))}
              displayEmpty
              disabled={!filterInputs.province}
              sx={{ 
                backgroundColor: '#f5f5f5',
                borderRadius: '8px',
                fontSize: '0.9rem',
                height: '36px',
                '& .MuiOutlinedInput-notchedOutline': {
                  border: 'none'
                },
                '&:hover .MuiOutlinedInput-notchedOutline': {
                  border: 'none'
                },
                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                  border: 'none'
                },
                boxShadow: '0 2px 4px rgba(0,0,0,0.05)'
              }}
            >
              <MenuItem value="">
                <em>Seleccionar ciudad</em>
              </MenuItem>
              {availableCities.map((cityName) => (
                <MenuItem key={cityName} value={cityName}>
                  {cityName}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <Button
            variant="contained"
            onClick={handleViewMap}
            disabled={!filterInputs.province || loading}
            sx={{
              backgroundColor: '#FF6F00',
              color: 'white',
              '&:hover': {
                backgroundColor: '#F76707',
              },
              width: { xs: '100%', sm: 'auto' },
              fontWeight: 600,
              fontSize: '0.9rem',
              height: '36px',
              textTransform: 'none',
              borderRadius: '8px',
              boxShadow: '0 2px 4px rgba(0,0,0,0.05)',
              minWidth: '120px',
              '&.Mui-disabled': {
                backgroundColor: 'rgba(0,0,0,0.12)',
                color: 'rgba(0,0,0,0.38)'
              }
            }}
          >
            {loading ? 'Cargando...' : 'Ver Mapa'}
          </Button>
        </Box>
      </Box>

      {!isMobile && (
        <Divider 
          orientation="vertical" 
          flexItem 
          sx={{ 
            backgroundColor: 'rgba(0,0,0,0.1)',
            my: 2
          }} 
        />
      )}

      {/* Quick access options */}
      <Box
        sx={{
          display: 'flex',
          gap: 1,
          flex: 1,
          overflowX: 'auto',
          alignItems: 'center',
          width: '100%',
          flexWrap: { xs: 'wrap', md: 'nowrap' },
          '&::-webkit-scrollbar': {
            height: '4px',
          },
          '&::-webkit-scrollbar-track': {
            background: '#f5f5f5',
          },
          '&::-webkit-scrollbar-thumb': {
            background: '#ddd',
            borderRadius: '2px',
          },
        }}
      >
        {quickAccessOptions.map((option, index) => (
          <Tooltip key={index} title={option.tooltip} arrow>
            <Chip
              icon={option.icon}
              label={option.label}
              onClick={() => handleQuickAccess(option.key)}
              clickable
              sx={{
                backgroundColor: option.selected ? '#FF6F00' : '#f5f5f5',
                color: option.selected ? '#ffffff' : '#424242',
                '&:hover': {
                  backgroundColor: option.selected ? '#F76707' : '#eeeeee',
                  transform: 'translateY(-1px)',
                },
                minWidth: 'fit-content',
                flex: { xs: '1 1 auto', md: '0 1 auto' },
                fontWeight: 500,
                fontSize: '0.85rem',
                height: '36px',
                borderRadius: '8px',
                boxShadow: '0 2px 4px rgba(0,0,0,0.05)',
                transition: 'all 0.2s ease-in-out',
                '& .MuiSvgIcon-root': {
                  color: option.selected ? '#ffffff' : '#2196F3',
                  fontSize: '18px'
                }
              }}
            />
          </Tooltip>
        ))}
      </Box>

      {!isMobile && (
      <Box sx={{ 
        display: 'flex', 
        alignItems: 'center',
        gap: 1,
        ml: { md: 'auto' },
        width: { xs: '100%', md: 'auto' }
      }}>
        <Typography variant="body2" sx={{ color: '#424242', fontSize: '0.9rem' }}>
          Ordenar por
        </Typography>
        <FormControl size="small">
          <Select
            value={`${searchParams.get('sortBy') || 'createdAt'}-${searchParams.get('sortOrder') || 'desc'}`}
            onChange={(e) => {
              const [sortBy, sortOrder] = e.target.value.split('-');
              const newParams = new URLSearchParams(searchParams);
              newParams.set('sortBy', sortBy);
              newParams.set('sortOrder', sortOrder);
              setSearchParams(newParams);
            }}
            sx={{ 
              backgroundColor: '#f5f5f5',
              borderRadius: '8px',
              fontSize: '0.9rem',
              height: '36px',
              minWidth: '150px',
              '& .MuiOutlinedInput-notchedOutline': {
                border: 'none'
              },
              '&:hover .MuiOutlinedInput-notchedOutline': {
                border: 'none'
              },
              '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                border: 'none'
              },
              boxShadow: '0 2px 4px rgba(0,0,0,0.05)'
            }}
          >
            <MenuItem value="createdAt-desc">Más recientes</MenuItem>
            <MenuItem value="createdAt-asc">Más antiguos</MenuItem>
            <MenuItem value="basePrice-asc">Menor precio</MenuItem>
            <MenuItem value="basePrice-desc">Mayor precio</MenuItem>
            <MenuItem value="name-asc">Nombre A-Z</MenuItem>
            <MenuItem value="name-desc">Nombre Z-A</MenuItem>
          </Select>
        </FormControl>
      </Box>
      )}

      {  isMobile &&
      <Box>
        <NotFoundDialog />
      <MapDialog 
        open={mapOpen}
        onClose={() => setMapOpen(false)}
        distributors={distributors}
        province={filterInputs.province}
        city={filterInputs.city}
        category={filterInputs.category}
      />
      </Box>
      }

    </Box>
  );

  if (isMobile) {
    return (
      <Box
        sx={{
          width: '100%',
          backgroundColor: 'white',
          p: 1.5,
          borderBottom: '1px solid rgba(0, 0, 0, 0.06)',
        }}
      >
        <Box 
          onClick={() => setIsExpanded(!isExpanded)}
          sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'space-between',
            cursor: 'pointer'
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <MapIcon sx={{ color: '#FF6F00' }} />
            <Typography>Ver mapa de distribuidores</Typography>
          </Box>
          <IconButton size="small">
            {isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          </IconButton>
        </Box>
        <Collapse in={isExpanded}>
          {toolbarContent}
        </Collapse>
      </Box>
    );
  }

  // Desktop version with adjusted padding
  return (
    <>
      <Box
        sx={{
            width: '100%',
            backgroundColor: 'white',
            borderRadius: '12px',
            py: 1,
            px: 0,
            mt: 1,
            mb: isMobile ? 0 : 2,
            border: '1px solid rgba(0, 0, 0, 0.06)',
        }}
      >
        {toolbarContent}
      </Box>

      <NotFoundDialog />
      <MapDialog 
        open={mapOpen}
        onClose={() => setMapOpen(false)}
        distributors={distributors}
        province={filterInputs.province || searchParams.get('province')}
        city={filterInputs.city || searchParams.get('city')}
        category={filterInputs.category || searchParams.get('category')}
      />
    </>
  );
};

export default QuickAccessToolbar; 
